import React, {FC, useEffect, useMemo, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../../auth'
import {getCompanyAdminList, getCompanyList} from '../../Management/core/_requests'
import {toast, ToastContainer} from 'react-toastify'
import html2pdf from 'html2pdf.js'

import type {Vessel} from '../../Management/core/_models'
import type {
  QHSENearMissReportDto,
  NearMissRecord,
  NearMissOccurrenceType,
  NearMissStatus,
} from '../core/_models'

import {
  searchNearMissReports,
  mapNearMissToRow,
  createNearMissReport,
  getNearMissReport,
  updateNearMissReport,
  uploadNearMissSupportingDocuments,
  nearMissSupportingViewUrl,
  nearMissSupportingDownloadUrl,
  nearMissSupportingFileViewUrl,
  nearMissSupportingFileDownloadUrl,
  listNearMissSupportingDocuments,
  type NearMissAttachmentInfo,
  submitNearMissReport,
  closeNearMissReport,
} from '../core/_requests'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'

import {FileViewerModal} from '../components/FileViewerModal'
import {AddNearMissModal, NearMissSubmitShape} from './AddNearMissModal'
import {ViewNearMissModal} from './ViewNearMissModal'
import {EditNearMissModal} from './EditNearMissModal'
import {NearMissClosureModal} from './NearMissClosureModal'
import NearMissAttachmentsModal from './NearMissAttachmentsModal'
import {NearMissSubmitModal} from './NearMissSubmitModal'
import {QHSE_PERMS} from '../core/permissions'
import {
  filterVesselsForManagementList,
  getUserCompanyGroupAdminId,
  isBroadFleetPickerUser,
  isLikelyVesselAssignedUser,
} from '../core/qhseUserContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'

const toastApiError = (e: any, fallback: string) => {
  if (e?.response?.status === 403) {
    toast.error('You do not have access to perform this action.', {position: 'top-center'})
    return
  }
  toast.error(fallback, {position: 'top-center'})
}


type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

const tableOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(255,255,255,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 5,
}

const spinner = (
  <div className='spinner-border' role='status' aria-label='Loading'>
    <span className='visually-hidden'>Loading...</span>
  </div>
)

const statusBadgeClass = (status?: string | null, shipboardUser?: boolean) => {
  if (!status) return 'badge-light'

  switch (status) {
    case 'SAVE':
      return 'badge-light-secondary'
    case 'SUBMIT':
      return shipboardUser ? 'badge-light-danger' : 'badge-light-warning'
    case 'CLOSE':
      return 'badge-light-success'
    default:
      return 'badge-light'
  }
}

const NearMissReports: FC = () => {
  const {currentUser, can, permissionsLoaded, isSuperAdmin} = useAuth()

  const shipboardUser = isLikelyVesselAssignedUser(currentUser)
  const broadFleet = isBroadFleetPickerUser(currentUser)
  const myCgaId = getUserCompanyGroupAdminId(currentUser)
  const isTopLevel = broadFleet
  const effectiveCompanyId = isTopLevel ? undefined : (myCgaId ?? undefined)

  const canReadNearMiss = permissionsLoaded && can(QHSE_PERMS.NEAR_MISS_READ)
  const canCreateNearMiss = permissionsLoaded && can(QHSE_PERMS.NEAR_MISS_CREATE)
  const canUpdateNearMiss = permissionsLoaded && can(QHSE_PERMS.NEAR_MISS_UPDATE)
  const canSubmitNearMiss = permissionsLoaded && can(QHSE_PERMS.NEAR_MISS_SUBMIT)
  const canCloseNearMiss = permissionsLoaded && can(QHSE_PERMS.NEAR_MISS_CLOSE)
  const canExportNearMiss = permissionsLoaded && can(QHSE_PERMS.NEAR_MISS_READ)
  const canOpenSupportingDocs =
    permissionsLoaded &&
    (can(QHSE_PERMS.NEAR_MISS_SUPPORT_VIEW) || can(QHSE_PERMS.NEAR_MISS_SUPPORT_DOWNLOAD))

  // Filters
  const [filters, setFilters] = useState<{
    companyId: string
    subcompanyId: string
    vesselId: string
    occurrenceType: string
    status: string
    fromDate: string
    toDate: string
  }>({
    companyId: '',
    subcompanyId: '',
    vesselId: '',
    occurrenceType: '',
    status: '',
    fromDate: '',
    toDate: '',
  })

  const [searchTerm, setSearchTerm] = useState('')

  // Lookups
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [subcompanies, setSubcompanies] = useState<Subcompany[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [vesselsScoped, setVesselsScoped] = useState<Vessel[]>([])
  const [loadingLookups, setLoadingLookups] = useState(false)
  const [lookupsReady, setLookupsReady] = useState(false)

  const [vesselMetaMap, setVesselMetaMap] = useState<
    Record<number, {name: string; companyGroupAdminId?: number | null; companyAdminId?: number | null}>
  >({})

  // data
  const [records, setRecords] = useState<NearMissRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [bootLoading, setBootLoading] = useState(true)
  const [noDataDelayPassed, setNoDataDelayPassed] = useState(false)

  // pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // sorting
  const [sortColumn, setSortColumn] = useState<keyof NearMissRecord | ''>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [didAutoSelectVessel, setDidAutoSelectVessel] = useState(false)

  // modals
  const [addOpen, setAddOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [viewing, setViewing] = useState<QHSENearMissReportDto | null>(null)
  const [editing, setEditing] = useState<QHSENearMissReportDto | null>(null)

  const [closingReport, setClosingReport] = useState<NearMissRecord | null>(null)
  const [submitId, setSubmitId] = useState<number | null>(null)
  const [fileModal, setFileModal] = useState<{
    visible: boolean
    title: string
    fileName?: string
    viewUrl?: string
    downloadUrl?: string
  }>({
    visible: false,
    title: '',
  })

  const [attachmentsModal, setAttachmentsModal] = useState<{
    visible: boolean
    reportNumber: string | null
    loading: boolean
    items: {fileName: string; viewUrl: string; downloadUrl: string}[]
  }>({
    visible: false,
    reportNumber: null,
    loading: false,
    items: [],
  })

  const openSupportingListModal = async (id: number, reportNumber?: string | null) => {
    setFileModal(prev => ({...prev, visible: false}))
    const label = reportNumber ? String(reportNumber) : String(id)

    setAttachmentsModal({
      visible: true,
      reportNumber: label,
      loading: true,
      items: [],
    })

    try {
      const list = await listNearMissSupportingDocuments(id)
      const items = list.map((it: NearMissAttachmentInfo) => ({
        fileName: it.fileName,
        viewUrl: nearMissSupportingFileViewUrl(id, it.fileName),
        downloadUrl: nearMissSupportingFileDownloadUrl(id, it.fileName),
      }))
      setAttachmentsModal({
        visible: true,
        reportNumber: label,
        loading: false,
        items,
      })
    } catch (e) {
      console.error('Failed to load near miss attachments', e)
      toastApiError(e, 'Failed to load attachments')
      setAttachmentsModal(prev => ({...prev, loading: false}))
    }
  }

  const renderStatusLabel = (r: NearMissRecord): string => {
    if (!r.status) return '-'
    if (r.status === 'SAVE') return 'Saved'
    if (r.status === 'SUBMIT') {
      return shipboardUser ? 'Submitted' : 'Ready for Review'
    }
    if (r.status === 'CLOSE') {
      if (r.closureResult === 'TO_BE_REVIEWED_NEXT_INSPECTION') {
        return 'Closed and TBR'
      }
      return 'Closed Satisfactorily'
    }
    return r.statusLabel || '-'
  }

  // ==========================
  // Lookups
  // ==========================

  useEffect(() => {
    const loadLookups = async () => {
      setLoadingLookups(true)
      try {
        const [groupsRaw, adminsRaw, vesselList] = await Promise.all([
          getCompanyAdminList?.().catch(() => []),
          getCompanyList?.().catch(() => []),
          fetchAccessibleVesselsForCurrentUser().then(normalizeAccessibleVessels).catch(() => [] as Vessel[]),
        ])

        const groups: CompanyGroup[] = Array.isArray(groupsRaw)
          ? groupsRaw.map((g: any) => ({
              id: Number(g.id),
              name: g.name ?? g.companyGroupAdminName ?? `Group #${g.id}`,
            }))
          : []

        const admins: Subcompany[] = Array.isArray(adminsRaw)
          ? adminsRaw.map((a: any) => ({
              id: Number(a.id),
              name: a.name ?? a.companyAdminName ?? `Company #${a.id}`,
              companyId: Number(
                a.cgaid?.id ??
                a.cga?.id ??
                a.companyGroupAdminId ??
                a.companyGroupId
              ),
            }))
          : []

        setCompanies(groups.filter(g => Number.isFinite(g.id)))
        setSubcompanies(admins.filter(a => Number.isFinite(a.id) && Number.isFinite(a.companyId)))

        const vesselsForUser = filterVesselsForManagementList(vesselList, currentUser)

        setVessels(vesselList)
        setVesselsScoped(vesselsForUser)

        const meta: Record<number, {name: string; companyGroupAdminId?: number | null; companyAdminId?: number | null}> = {}
        for (const v of vesselList as any[]) {
          const id = Number(v.id)
          meta[id] = {
            name: v.fleet_name || v.name || `Vessel ${id}`,
            companyGroupAdminId: Number(
              v.companyGroupAdmin?.id ??
              v.companyGroupId ??
              v.cgaid?.id
            ) || null,
            companyAdminId: Number(
              v.companyAdmin?.id ??
              v.companyId
            ) || null,
          }
        }
        setVesselMetaMap(meta)
      } catch (e) {
        console.error('Failed to load near miss lookups', e)
        setCompanies([])
        setSubcompanies([])
        setVessels([])
        setVesselsScoped([])
        setVesselMetaMap({})
      } finally {
        setLoadingLookups(false)
        setLookupsReady(true)
      }
    }

    loadLookups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser])

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.searchQuery !== undefined) {
      setSearchTerm(globalState.searchQuery)
    }
  }, [globalState.searchQuery])

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      vesselId: globalState.vessel?.id ? String(globalState.vessel.id) : '',
      companyId: globalState.company ? String(globalState.company.id) : '',
    }))
  }, [globalState.vessel, globalState.company])

  const vesselsForFilters = useMemo(() => {
    let list = vesselsScoped
    if (filters.companyId) {
      const cid = Number(filters.companyId)
      list = list.filter((v: any) =>
        Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid
      )
    }
    if (filters.subcompanyId) {
      const scid = Number(filters.subcompanyId)
      list = list.filter((v: any) =>
        Number(v.companyAdmin?.id ?? v.companyId) === scid
      )
    }
    return list
  }, [vesselsScoped, filters.companyId, filters.subcompanyId])

  const subcompaniesForChosenCompany = useMemo(() => {
    if (!filters.companyId) return []
    const cid = Number(filters.companyId)
    return subcompanies.filter(sc => sc.companyId === cid)
  }, [filters.companyId, subcompanies])

  const hasSubcompaniesForChosenCompany = subcompaniesForChosenCompany.length > 0

  // Auto-select default vessel for shipboard users only
  useEffect(() => {
    if (!lookupsReady) return
    if (shipboardUser && !didAutoSelectVessel) {
      const vesselId = currentUser?.vessel?.id
      if (vesselId) {
        setFilters(prev => ({...prev, vesselId: String(vesselId)}))
        setDidAutoSelectVessel(true)
      }
    }
  }, [lookupsReady, shipboardUser, didAutoSelectVessel, currentUser])

  // ==========================
  // Load near miss reports
  // ==========================

  const loadNearMissReports = async () => {
    if (!lookupsReady) return
    setLoading(true)
    try {
      const params = {
        companyGroupId: filters.companyId
          ? Number(filters.companyId)
          : effectiveCompanyId,
        companyId: filters.subcompanyId ? Number(filters.subcompanyId) : undefined,
        vesselId: filters.vesselId ? Number(filters.vesselId) : undefined,
        occurrenceType: filters.occurrenceType
          ? (filters.occurrenceType as NearMissOccurrenceType)
          : undefined,
        status: filters.status
          ? (filters.status as NearMissStatus)
          : undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
      }

      const list = await searchNearMissReports(params)
      const mapped: NearMissRecord[] = list.map(d => {
        const meta = vesselMetaMap[d.vesselId] || {}
        return mapNearMissToRow(
          {
            ...d,
            vesselName: d.vesselName || meta.name || '-',
          },
          {
            companyGroupAdminId: meta.companyGroupAdminId ?? null,
            companyAdminId: meta.companyAdminId ?? null,
          }
        )
      })
      setRecords(mapped)
    } catch (e) {
      console.error('Failed to load near miss reports', e)
      setRecords([])
      toastApiError(e, 'Failed to load near miss reports')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!lookupsReady) return
    ;(async () => {
      setBootLoading(true)
      try {
        await loadNearMissReports()
      } finally {
        setBootLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupsReady, effectiveCompanyId])

  useEffect(() => {
    if (!lookupsReady) return
    loadNearMissReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.companyId,
    filters.subcompanyId,
    filters.vesselId,
    filters.occurrenceType,
    filters.status,
    filters.fromDate,
    filters.toDate,
  ])

  useEffect(() => {
    if (bootLoading || loading) {
      setNoDataDelayPassed(false)
      return
    }
    const t = setTimeout(() => setNoDataDelayPassed(true), 500)
    return () => clearTimeout(t)
  }, [bootLoading, loading])

  // ==========================
  // Handlers
  // ==========================

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {name, value} = e.target
    setFilters(prev => {
      const next = {...prev, [name]: value}
      if (name === 'companyId') {
        next.subcompanyId = ''
        next.vesselId = ''
      }
      if (name === 'subcompanyId') {
        next.vesselId = ''
      }
      return next
    })
  }

  const handleClearFilters = () => {
    setFilters({
      companyId: '',
      subcompanyId: '',
      vesselId: '',
      occurrenceType: '',
      status: '',
      fromDate: '',
      toDate: '',
    })
    setSearchTerm('')
    setCurrentPage(1)
  }

  const handleSort = (column: keyof NearMissRecord) => {
    setSortColumn(prev => {
      if (prev === column) {
        setSortOrder(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setSortOrder('asc')
      return column
    })
  }

  const filteredData = useMemo(() => {
    return records.filter(r => {
      const matchesSearch =
        (r.vesselName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.locationPosition || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.descriptionOfOccurrence || '').toLowerCase().includes(searchTerm.toLowerCase())

      const byCompany =
        !filters.companyId ||
        Number(r.companyGroupAdminId) === Number(filters.companyId)

      const bySubcompany =
        !filters.subcompanyId ||
        Number(r.companyAdminId) === Number(filters.subcompanyId)

      const byVessel =
        !filters.vesselId ||
        Number(r.vesselId) === Number(filters.vesselId)

      const byOccurrence =
        !filters.occurrenceType ||
        r.occurrenceTypes.includes(filters.occurrenceType as NearMissOccurrenceType)

      const byStatus =
        !filters.status ||
        r.status === (filters.status as NearMissStatus)

      const byFromDate =
        !filters.fromDate ||
        (!r.dateOfOccurrence || new Date(r.dateOfOccurrence) >= new Date(filters.fromDate))

      const byToDate =
        !filters.toDate ||
        (!r.dateOfOccurrence || new Date(r.dateOfOccurrence) <= new Date(filters.toDate))

      return (
        matchesSearch &&
        byCompany &&
        bySubcompany &&
        byVessel &&
        byOccurrence &&
        byStatus &&
        byFromDate &&
        byToDate
      )
    })
  }, [records, searchTerm, filters])

  const sortedData = useMemo(() => {
    const data = [...filteredData]

    data.sort((a, b) => {
      if (sortColumn) {
        if (
          sortColumn === 'dateOfOccurrence' ||
          sortColumn === 'dateReported' ||
          sortColumn === 'dateIssued'
        ) {
          const aTimeStr =
            sortColumn === 'dateOfOccurrence'
              ? (a.dateOfOccurrence ? `${a.dateOfOccurrence} ${a.timeOfOccurrence || '00:00'}` : '')
              : sortColumn === 'dateReported'
              ? (a.dateReported ? `${a.dateReported} ${a.timeReported || '00:00'}` : '')
              : (a.dateIssued ? String(a.dateIssued) : '')

          const bTimeStr =
            sortColumn === 'dateOfOccurrence'
              ? (b.dateOfOccurrence ? `${b.dateOfOccurrence} ${b.timeOfOccurrence || '00:00'}` : '')
              : sortColumn === 'dateReported'
              ? (b.dateReported ? `${b.dateReported} ${b.timeReported || '00:00'}` : '')
              : (b.dateIssued ? String(b.dateIssued) : '')

          const aTime = aTimeStr ? new Date(aTimeStr).getTime() : 0
          const bTime = bTimeStr ? new Date(bTimeStr).getTime() : 0

          const diff = aTime - bTime
          if (diff !== 0) {
            return sortOrder === 'asc' ? diff : -diff
          }
          // Tie-breaker: newest entry (highest ID) always first
          return (Number(b.id) || 0) - (Number(a.id) || 0)
        }

        const aVal = a[sortColumn]
        const bVal = b[sortColumn]

        const aStr = (aVal ?? '').toString().toLowerCase()
        const bStr = (bVal ?? '').toString().toLowerCase()

        if (aStr !== bStr) {
          if (aStr < bStr) return sortOrder === 'asc' ? -1 : 1
          if (aStr > bStr) return sortOrder === 'asc' ? 1 : -1
        }

        // Secondary tie-breaker: newest entry (highest ID) always first
        return (Number(b.id) || 0) - (Number(a.id) || 0)
      }

      // Default sort (no column selected): newest entry (highest ID) first
      return (Number(b.id) || 0) - (Number(a.id) || 0)
    })

    return data
  }, [filteredData, sortColumn, sortOrder])

  const showVesselColumn = !filters.vesselId
  const columnCount = showVesselColumn ? 11 : 10

  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page)
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setCurrentPage(1)
  }

  const handleExportPdf = () => {
    try {
      if (!canExportNearMiss) {
        toast.error('You do not have permission to export reports.', {position: 'top-center'})
        return
      }
      if (!sortedData.length) {
        toast.warning('No near miss reports to export for the selected filters', {
          position: 'top-center',
        })
        return
      }

      const now = new Date()
      const headerText = 'Near Miss Reports'
      const subHeaderText = `Exported on ${now.toLocaleDateString()}`

      const headers: string[] = [
        'Sr/No',
        'Report No.',
      ]

      if (showVesselColumn) {
        headers.push('Vessel')
      }

      headers.push(
  'Occurrence Types',
  'Date of Occurrence',
  'Date of Report',
  'Location',
  'Reported By',
  'Status',
)

      let html = `
        <div style="font-family: Arial, sans-serif; font-size: 9px; width: 100%;">
          <h3 style="text-align:center; margin: 0 0 4px 0;">${headerText}</h3>
          <div style="text-align:center; margin-bottom: 8px;">${subHeaderText}</div>
          <table style="border-collapse: collapse; width: 100%; table-layout: fixed;">
            <thead>
              <tr>
                ${headers
                  .map(
                    (h) =>
                      `<th style="border: 1px solid #999; padding: 4px; word-wrap: break-word; background:#f5f5f5;">${h}</th>`
                  )
                  .join('')}
              </tr>
            </thead>
            <tbody>
      `

      sortedData.forEach((r, index) => {
        const cells: string[] = []

        cells.push(String(index + 1))
        cells.push(r.reportNumber || '-')

        if (showVesselColumn) {
          cells.push(r.vesselName || '-')
        }

        cells.push(r.occurrenceTypesLabel || '-')
cells.push(r.dateOfOccurrence || '-')
cells.push(r.dateReported || '-')  // new column for report date
cells.push(r.locationPosition || '-')
cells.push(r.reportedBy || '-')
cells.push(renderStatusLabel(r))

        html += `
          <tr>
            ${cells
              .map(
                (c) =>
                  `<td style="border: 1px solid #999; padding: 4px; vertical-align: top; word-wrap: break-word;">${c}</td>`
              )
              .join('')}
          </tr>
        `
      })

      html += `
            </tbody>
          </table>
        </div>
      `

      const element = document.createElement('div')
      element.innerHTML = html

      const opt = {
        margin: [10, 10, 10, 10],
        filename: 'near-miss-reports.pdf',
        image: {type: 'jpeg', quality: 0.98},
        html2canvas: {scale: 2, useCORS: true},
        jsPDF: {unit: 'pt', format: 'a4', orientation: 'landscape'},
      }

      // @ts-ignore – html2pdf typings
      html2pdf().set(opt).from(element).save()
    } catch (err) {
      console.error('Export PDF failed', err)
      toastApiError(err, 'Failed to export PDF')
    }
  }

  // -----------------------
  // Individual Report PDF Download
  // -----------------------

  const [downloadingPdfId, setDownloadingPdfId] = useState<number | null>(null)

  const handleDownloadIndividualReportPdf = async (id: number) => {
    if (!canExportNearMiss) {
      toast.error('You do not have permission to export reports.', {position: 'top-center'})
      return
    }
    setDownloadingPdfId(id)
    try {
      const dto = await getNearMissReport(id)
      const meta = vesselMetaMap[dto.vesselId] || {}
      const report: QHSENearMissReportDto = {
        ...dto,
        vesselName: dto.vesselName || meta.name || '-',
      }

      const occurrenceTypeLabel = (t: string): string => {
        switch (t) {
          case 'ACCIDENT': return 'Accident'
          case 'INCIDENT': return 'Incident'
          case 'NEAR_MISS': return 'Near Miss'
          case 'HIGH_SEVERITY_NEAR_MISS': return 'High Severity Near Miss'
          default: return t
        }
      }

      const locationOfOccurrenceLabel = (loc: string): string => {
        switch (loc) {
          case 'ACCOMMODATION': return 'Accommodation'
          case 'BRIDGE': return 'Bridge'
          case 'ASHORE': return 'Ashore'
          case 'ENGINE_ROOM_MACHINERY_SPACES': return 'Engine Room / Machinery Spaces'
          case 'DECK': return 'Deck'
          case 'ALOFT_OVERBOARD': return 'Aloft / Overboard'
          case 'ENCLOSED_SPACES': return 'Enclosed Spaces'
          case 'ENCLOSED_SPACES_MACHINERY': return 'Enclosed Spaces (Machinery)'
          case 'OFFICE': return 'Office'
          case 'STORE': return 'Store'
          case 'TRAFFIC_ROAD': return 'Traffic Road'
          case 'OTHER': return 'Other'
          default: return loc
        }
      }

      const substandardActLabel = (a: string): string => {
        switch (a) {
          case 'FAILURE_TO_FOLLOW_RULES': return 'Failure To Follow Rules'
          case 'FAILURE_TO_USE_PPE_PROPERLY': return 'Failure To Use PPE Properly'
          case 'OPERATING_EQUIPMENT_WITHOUT_AUTHORITY': return 'Operating Equipment Without Authority'
          case 'INCORRECT_USE_OF_EQUIPMENT_OR_MACHINERY': return 'Incorrect Use of Equipment / Machinery'
          case 'USE_OF_DEFECTIVE_EQUIPMENT_OR_MACHINERY': return 'Use Of Defective Equipment / Machinery'
          case 'FAILURE_TO_FOLLOW_REPAIR_OR_MAINTENANCE_INSTRUCTIONS': return 'Failure To Follow Repair / Maintenance Instructions'
          case 'FAILURE_TO_WARN': return 'Failure To Warn'
          case 'FAILURE_TO_SECURE': return 'Failure To Secure'
          case 'BY_PASSING_SAFETY_DEVICE': return 'By-Passing Safety Device'
          case 'IMPROPER_POSITION_FOR_TASK': return 'Improper Position for Task'
          case 'IMPROPER_LIFTING_HANDLING_OR_STORAGE': return 'Improper Lifting, Handling or Storage'
          case 'HORSEPLAY_OR_INAPPROPRIATE_BEHAVIOUR': return 'Horseplay / Inappropriate Behaviour'
          case 'UNDER_INFLUENCE_OF_ALCOHOL_OR_DRUGS': return 'Under Influence of Alcohol or Drugs'
          default: return a
        }
      }

      const substandardConditionLabel = (c: string): string => {
        switch (c) {
          case 'INADEQUATE_GUARDS_OR_BARRIERS': return 'Inadequate Guards or Barriers'
          case 'INADEQUATE_OR_DEFECTIVE_PPE': return 'Inadequate Or Defective PPE'
          case 'DEFECTIVE_EQUIPMENT_OR_MACHINERY': return 'Defective Equipment / Machinery'
          case 'UNFAVOURABLE_HULL_OR_STRUCTURE_CONDITION': return 'Unfavourable Hull or Structure Condition'
          case 'POOR_HOUSEKEEPING': return 'Poor Housekeeping'
          case 'CONGESTION_OR_RESTRICTED_ACTION': return 'Congestion or Restricted Action'
          case 'INADEQUATE_OR_EXCESS_ILLUMINATION': return 'Inadequate or Excess Illumination'
          case 'INADEQUATE_VENTILATION': return 'Inadequate Ventilation'
          case 'OTHER_FACTORS': return 'Other Factors'
          default: return c
        }
      }

      const statusLabel = (): string => {
        if (!report.status) return '-'
        switch (report.status) {
          case 'SAVE': return 'Saved'
          case 'SUBMIT': return 'Submitted'
          case 'CLOSE':
            return report.closureResult === 'TO_BE_REVIEWED_NEXT_INSPECTION'
              ? 'Closed and TBR'
              : 'Closed Satisfactorily'
          default: return report.status
        }
      }
      const row = (label: string, value: string | null | undefined) =>
        `<tr style="page-break-inside: avoid;">
          <td style="border:1px solid #ccc;padding:6px 10px;font-weight:600;background:#f9f9f9;width:35%;">${label}</td>
          <td style="border:1px solid #ccc;padding:6px 10px;">${value || '-'}</td>
        </tr>`

      const section = (title: string, rows: string) =>
        `<div style="page-break-inside: avoid; margin-bottom: 10px;">
          <table style="border-collapse: collapse; width: 100%;">
            <tr style="page-break-inside: avoid;">
              <td colspan="2" style="background:#1a3c6e;color:#fff;font-weight:700;padding:8px 10px;font-size:11px;letter-spacing:0.5px;">${title}</td>
            </tr>
            ${rows}
          </table>
        </div>`

      const descSection = (title: string, content: string) =>
        `<div style="page-break-inside: avoid; margin-bottom: 10px;">
          <table style="border-collapse: collapse; width: 100%;">
            <tr style="page-break-inside: avoid;">
              <td colspan="2" style="background:#1a3c6e;color:#fff;font-weight:700;padding:8px 10px;font-size:11px;letter-spacing:0.5px;">${title}</td>
            </tr>
            <tr style="page-break-inside: avoid;">
              <td colspan="2" style="border:1px solid #ccc;padding:8px 10px;white-space:pre-wrap;">${content || '-'}</td>
            </tr>
          </table>
        </div>`

      const html = `
        <div style="font-family: Arial, sans-serif; font-size: 10px; width: 100%; color: #333;">

          <!-- Header Banner -->
          <div style="background: #1a3c6e; color: #fff; padding: 16px 20px; margin-bottom: 16px; border-radius: 4px; page-break-inside: avoid;">
            <div style="font-size: 18px; font-weight: 700; margin-bottom: 4px;">Near Miss Report</div>
            <div style="font-size: 12px; opacity: 0.85;">
              Report No: <strong>${report.reportNumber || '-'}</strong>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              Vessel: <strong>${report.vesselName || '-'}</strong>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              Status: <strong>${statusLabel()}</strong>
              &nbsp;&nbsp;|&nbsp;&nbsp;
              Exported: ${new Date().toLocaleDateString()}
            </div>
          </div>

          ${section('Report Details',
            row('Report Number', report.reportNumber) +
            row('Vessel', report.vesselName) +
            row('Status', statusLabel()) +
            row('Report Issued By', report.reportIssuedBy) +
            row('Date Issued', report.dateIssued) +
            row('Reported By', report.reportedBy) +
            row('Reported To', report.reportedTo) +
            row('Date Reported', report.dateReported ? `${report.dateReported}${report.timeReported ? ' ' + report.timeReported : ''}` : '-')
          )}

          ${section('Occurrence Details',
            row('Date of Occurrence', report.dateOfOccurrence ? `${report.dateOfOccurrence}${report.timeOfOccurrence ? ' ' + report.timeOfOccurrence : ''}` : '-') +
            row('Position / Location', report.locationPosition) +
            row('Occurrence Types', report.occurrenceTypes?.map(occurrenceTypeLabel).join(', ') || '-') +
            row('Conditions', [report.substandardActs?.length ? 'Unsafe Act' : '', report.substandardConditions?.length ? 'Unsafe Condition' : ''].filter(Boolean).join(', ') || '-') +
            row('Location of Occurrence', report.locationOfOccurrence?.map(locationOfOccurrenceLabel).join(', ') || '-') +
            row('Personal Injury Form Completed', report.personalInjuryFormCompleted == null ? '-' : report.personalInjuryFormCompleted ? 'Yes' : 'No') +
            row('Any Statements Attached', report.anyStatementsAttached == null ? '-' : report.anyStatementsAttached ? 'Yes' : 'No')
          )}

          ${descSection('Description of Occurrence', report.descriptionOfOccurrence || '-')}

          ${section('Analysis of Occurrence',
            row('Substandard Acts', report.substandardActs?.map(substandardActLabel).join(', ') || '-') +
            row('Substandard Conditions', report.substandardConditions?.map(substandardConditionLabel).join(', ') || '-')
          )}

          ${section('Persons Involved',
            row('Persons Injured', report.personsInjured) +
            row('Persons Involved', report.personsInvolved) +
            row('Persons Witness', report.personsWitness) +
            row('Type of Injury', report.typeOfInjury)
          )}

          ${section('Causes & Corrective Actions',
            row('Immediate / Basic Cause', report.immediateBasicCause) +
            row('Root Cause', report.rootCause) +
            row('Corrective Actions Proposed', report.correctiveActionsProposed) +
            row('Preventative Actions Proposed', report.preventativeActionsProposed)
          )}

          ${section('Review',
            row('Objective Evidence', report.objectiveEvidence) +
            row('DPA Conclusions & Recommendations', report.dpaConclusionsAndRecommendations)
          )}

          <div style="margin-top: 8px; font-size: 9px; color: #888; border-top: 1px solid #ddd; padding-top: 6px; page-break-inside: avoid;">
            This report was generated by Dexpert Fleet Management System on ${new Date().toLocaleString()}
          </div>
        </div>
      `

      const element = document.createElement('div')
      element.innerHTML = html

      const opt = {
        margin: [12, 12, 12, 12],
        filename: `near-miss-report-${report.reportNumber || id}.pdf`,
        image: {type: 'jpeg', quality: 0.98},
        html2canvas: {scale: 2, useCORS: true, logging: false},
        jsPDF: {unit: 'pt', format: 'a4', orientation: 'portrait'},
        pagebreak: {mode: ['avoid-all', 'css'], avoid: ['tr', 'div', 'table']},
      }

      // @ts-ignore – html2pdf typings
      await html2pdf().set(opt).from(element).save()
    } catch (err) {
      console.error('Individual report PDF download failed', err)
      toastApiError(err, 'Failed to download report PDF')
    } finally {
      setDownloadingPdfId(null)
    }
  }

  // -----------------------
  // Modals: View / Edit / Add
  // -----------------------

  const openView = async (id: number) => {
    try {
      const dto = await getNearMissReport(id)
      const meta = vesselMetaMap[dto.vesselId] || {}
      setViewing({
        ...dto,
        vesselName: dto.vesselName || meta.name || '-',
      })
      setViewOpen(true)
    } catch (e) {
      console.error('Failed to load near miss report', e)
      toastApiError(e, 'Failed to load near miss report')
    }
  }

  const openEdit = async (id: number) => {
    try {
      const dto = await getNearMissReport(id)
      const meta = vesselMetaMap[dto.vesselId] || {}
      setEditing({
        ...dto,
        vesselName: dto.vesselName || meta.name || '-',
      })
      setEditOpen(true)
    } catch (e) {
      console.error('Failed to load near miss report', e)
      toastApiError(e, 'Failed to load near miss report')
    }
  }

  const handleSubmitNearMissRow = async (id: number) => {
    try {
      await submitNearMissReport(id)
      toast.success('Near miss report submitted successfully', {position: 'top-center'})
      await loadNearMissReports()
    } catch (e: any) {
      console.error('Submit near miss failed', e)
      toastApiError(e, 'Failed to submit near miss report')
    }
  }

  const handleAddNearMiss = async (form: NearMissSubmitShape) => {
    if (!form.vesselId) {
      toast.error('Please select a vessel', {position: 'top-center'})
      return
    }
    if (!form.occurrenceTypes || form.occurrenceTypes.length === 0) {
      toast.error('Please select at least one occurrence type', {position: 'top-center'})
      return
    }

    const payload: Omit<
      QHSENearMissReportDto,
      'id' | 'createdAt' | 'updatedAt' | 'vesselName' | 'reportNumber' | 'status'
    > = {
      vesselId: Number(form.vesselId),
      locationPosition: form.locationPosition || undefined,
      reportIssuedBy: form.reportIssuedBy || undefined,
      dateIssued: form.dateIssued || undefined,

      dateOfOccurrence: form.dateOfOccurrence || undefined,
      timeOfOccurrence: form.timeOfOccurrence || undefined,

      reportedBy: form.reportedBy || undefined,
      reportedTo: form.reportedTo || undefined,
      dateReported: form.dateReported || undefined,
      timeReported: form.timeReported || undefined,

      occurrenceTypes: form.occurrenceTypes,

      personalInjuryFormCompleted: form.personalInjuryFormCompleted,
      anyStatementsAttached: form.anyStatementsAttached,

      locationOfOccurrence: form.locationOfOccurrence || [],

      descriptionOfOccurrence: form.descriptionOfOccurrence || undefined,

      substandardActs: form.substandardActs || [],
      substandardConditions: form.substandardConditions || [],

      personsInjured: form.personsInjured || undefined,
      personsInvolved: form.personsInvolved || undefined,
      personsWitness: form.personsWitness || undefined,
      typeOfInjury: form.typeOfInjury || undefined,

      immediateBasicCause: form.immediateBasicCause || undefined,
      rootCause: form.rootCause || undefined,
      correctiveActionsProposed: form.correctiveActionsProposed || undefined,
      preventativeActionsProposed: form.preventativeActionsProposed || undefined,

      objectiveEvidence: form.objectiveEvidence || undefined,
      supportingDocsPrimaryPath: undefined,

      dpaConclusionsAndRecommendations: form.dpaConclusionsAndRecommendations || undefined,

      active: true,
      closureResult: null,
    }

    try {
      const created = await createNearMissReport(payload)

      const reportId = Number(created.id)
      if (reportId && form.supportingFiles && form.supportingFiles.length > 0) {
        await uploadNearMissSupportingDocuments(reportId, form.supportingFiles)
      }

      toast.success('Near miss report created successfully', {position: 'top-center'})
      setCurrentPage(1)
      setSortColumn('')
      setSortOrder('desc')
      if (filters.status && filters.status !== 'SAVE' && filters.status !== 'SUBMIT') {
        setFilters(prev => ({...prev, status: ''}))
      }
      setAddOpen(false)
      await loadNearMissReports()
    } catch (e: any) {
      console.error('Create near miss failed', e)
      toastApiError(e, 'Failed to create near miss report')
    }
  }

  const handleUpdateNearMiss = async (form: NearMissSubmitShape) => {
    if (!editing?.id) return
    if (!form.occurrenceTypes || form.occurrenceTypes.length === 0) {
      toast.error('Please select at least one occurrence type', {position: 'top-center'})
      return
    }

    const partial: Partial<QHSENearMissReportDto> = {
// NOTE: vesselId is intentionally NOT updated.
    // Once a Near Miss is created, its vessel cannot be changed.
    // vesselId: Number(form.vesselId),
      locationPosition: form.locationPosition || undefined,
      reportIssuedBy: form.reportIssuedBy || undefined,
      dateIssued: form.dateIssued || undefined,

      dateOfOccurrence: form.dateOfOccurrence || undefined,
      timeOfOccurrence: form.timeOfOccurrence || undefined,

      reportedBy: form.reportedBy || undefined,
      reportedTo: form.reportedTo || undefined,
      dateReported: form.dateReported || undefined,
      timeReported: form.timeReported || undefined,

      occurrenceTypes: form.occurrenceTypes,
      personalInjuryFormCompleted: form.personalInjuryFormCompleted,
      anyStatementsAttached: form.anyStatementsAttached,
      locationOfOccurrence: form.locationOfOccurrence || [],

      descriptionOfOccurrence: form.descriptionOfOccurrence || undefined,

      substandardActs: form.substandardActs || [],
      substandardConditions: form.substandardConditions || [],

      personsInjured: form.personsInjured || undefined,
      personsInvolved: form.personsInvolved || undefined,
      personsWitness: form.personsWitness || undefined,
      typeOfInjury: form.typeOfInjury || undefined,

      immediateBasicCause: form.immediateBasicCause || undefined,
      rootCause: form.rootCause || undefined,
      correctiveActionsProposed: form.correctiveActionsProposed || undefined,
      preventativeActionsProposed: form.preventativeActionsProposed || undefined,

      objectiveEvidence: form.objectiveEvidence || undefined,
      dpaConclusionsAndRecommendations: form.dpaConclusionsAndRecommendations || undefined,
    }

    try {
      const updated = await updateNearMissReport(editing.id, partial)

      const reportId = Number(updated.id)
      if (reportId && form.supportingFiles && form.supportingFiles.length > 0) {
        await uploadNearMissSupportingDocuments(reportId, form.supportingFiles)
      }

      toast.success('Near miss report updated successfully', {position: 'top-center'})
      setEditOpen(false)
      setEditing(null)
      await loadNearMissReports()
    } catch (e: any) {
      console.error('Update near miss failed', e)
      toastApiError(e, 'Failed to update near miss report')
    }
  }

  const handleSaveReview = async (
    rec: NearMissRecord,
    files: File[] | null,
    dpaConclusions: string,
    objectiveEvidence: string
  ) => {
    try {
      if (files && files.length > 0) {
        await uploadNearMissSupportingDocuments(rec.id, files)
      }

      await updateNearMissReport(rec.id, {
        dpaConclusionsAndRecommendations: dpaConclusions,
        objectiveEvidence,
      })

      toast.success('Review saved successfully', {position: 'top-center'})
      await loadNearMissReports()
      return true
    } catch (e: any) {
      console.error('Save review failed', e)
      toastApiError(e, 'Failed to save review')
      return false
    }
  }

  const handleCloseWithResult = async (
    rec: NearMissRecord,
    files: File[] | null,
    dpaConclusions: string,
    objectiveEvidence: string,
    result: 'SATISFACTORY' | 'TO_BE_REVIEWED_NEXT_INSPECTION'
  ) => {
    try {
      if (files && files.length > 0) {
        await uploadNearMissSupportingDocuments(rec.id, files)
      }

      await updateNearMissReport(rec.id, {
        dpaConclusionsAndRecommendations: dpaConclusions,
        objectiveEvidence,
      })

      await closeNearMissReport(rec.id, result)

      toast.success('Near miss report closed successfully', {position: 'top-center'})
      setClosingReport(null)
      await loadNearMissReports()
      return true
    } catch (e: any) {
      console.error('Close near miss failed', e)
      toastApiError(e, 'Failed to close near miss report')
      return false
    }
  }

  const getOccurrenceOptions = () => [
    {value: '', label: 'All Occurrence Types'},
    {value: 'ACCIDENT', label: 'Accident'},
    {value: 'INCIDENT', label: 'Incident'},
    {value: 'NEAR_MISS', label: 'Near miss'},
    {value: 'HIGH_SEVERITY_NEAR_MISS', label: 'High severity near miss'},
  ]

  const getStatusOptions = () => [
    {value: '', label: 'All Status'},
    {value: 'SAVE', label: 'Saved'},
    {value: 'SUBMIT', label: 'Submitted / Ready for Review'},
    {value: 'CLOSE', label: 'Closed'},
  ]

  // -----------------------
  // Render
  // -----------------------

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            {/* Header */}
            <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
              <div>
                <h3 className='card-label text-dark fw-bold'>Near Miss Reports</h3>
              </div>
              <div className='card-toolbar d-flex gap-2'>
                {canExportNearMiss ? (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary me-2 btn-outline'
                    onClick={handleExportPdf}
                  >
                    <KTSVG
                      path='/media/icons/duotune/files/fil003.svg'
                      className='svg-icon-3 me-1'
                    />
                    Export PDF
                  </button>
                ) : null}

                {canCreateNearMiss ? (
                  <button
                    type='button'
                    className='btn btn_primary'
                    onClick={() => setAddOpen(true)}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    Add New Near Miss
                  </button>
                ) : null}
              </div>
            </div>

            {/* Filters */}
            <div className='card-body py-4 bg-white border-top'>
              <div className='row gx-3 gy-3 mb-4'>

                {/* Occurrence Type */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Occurrence Type
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='occurrenceType'
                    value={filters.occurrenceType}
                    onChange={handleFilterChange}
                  >
                    {getOccurrenceOptions().map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </SearchableSelect>
                </div>

                {/* Status */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Status
                  </label>
                  <SearchableSelect
                    className='form-select'
                    name='status'
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    {getStatusOptions().map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </SearchableSelect>
                </div>

                {/* Search text */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color: '#A1A5B7'}}>
                    Search
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder='Vessel / location / description'
                  />
                </div>

                <div className='col-md-2 d-flex align-items-end'>
                  <button
                    type='button'
                    className='btn btn-light me-2'
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                  {loadingLookups && (
                    <span className='text-muted small'>Loading lookups...</span>
                  )}
                </div>
              </div>

              <div className='row gx-3 gy-3 mb-4'>
                {/* Date From */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color:'#A1A5B7'}}>
                    From Date (Occurrence)
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    name='fromDate'
                    value={filters.fromDate}
                    onChange={handleFilterChange}
                  />
                </div>
                {/* Date To */}
                <div className='col-md-2'>
                  <label className='form-label fw-semibold fs-7' style={{color:'#A1A5B7'}}>
                    To Date (Occurrence)
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    name='toDate'
                    value={filters.toDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </div>

              {/* Table */}
              <div className='report-table table-responsive' style={{position: 'relative'}}>
                {loading && <div style={tableOverlayStyle}>{spinner}</div>}
                <div style={{overflowX: 'auto'}}>
                  <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        {/* Sr/No */}
                        <th className='text-center text-nowrap' style={{minWidth: '70px'}}>
                          Sr/No
                        </th>

                        {/* Report No */}
                        <th style={{minWidth: '140px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Report No.</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('reportNumber')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'reportNumber'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Vessel */}
                        {showVesselColumn && (
                          <th style={{minWidth: '180px'}}>
                            <div className='d-flex align-items-center text-nowrap'>
                              <span className='me-1'>Vessel</span>
                              <button
                                type='button'
                                className='btn btn-link p-0 m-0 pb-1'
                                onClick={() => handleSort('vesselName')}
                                disabled={records.length === 0}
                              >
                                <KTSVG
                                  path={`/media/map/sort-col-${
                                    sortColumn === 'vesselName'
                                      ? sortOrder === 'asc'
                                        ? 'up-black'
                                        : 'down-black'
                                      : 'grey'
                                  }.svg`}
                                  className='svg-icon-3'
                                />
                              </button>
                            </div>
                          </th>
                        )}

                        {/* Occurrence Types */}
                        <th style={{minWidth: '220px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Occurrence Types</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('occurrenceTypesLabel')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'occurrenceTypesLabel'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Date of Occurrence */}
                        <th style={{minWidth: '160px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Date of Occurrence</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('dateOfOccurrence')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'dateOfOccurrence'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Date of Report */}
<th style={{minWidth: '160px'}}>
  <div className='d-flex align-items-center text-nowrap'>
    <span className='me-1'>Date of Report</span>
    <button
      type='button'
      className='btn btn-link p-0 m-0 pb-1'
      onClick={() => handleSort('dateReported')}
      disabled={records.length === 0}
    >
      <KTSVG
        path={`/media/map/sort-col-${
          sortColumn === 'dateReported'
            ? sortOrder === 'asc'
              ? 'up-black'
              : 'down-black'
            : 'grey'
        }.svg`}
        className='svg-icon-3'
      />
    </button>
  </div>
</th>


                        {/* Location */}
                        <th style={{minWidth: '200px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Location</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('locationPosition')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'locationPosition'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Reported By */}
                        <th style={{minWidth: '160px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Reported By</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('reportedBy')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'reportedBy'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Status */}
                        <th style={{minWidth: '120px'}}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Status</span>
                            <button
                              type='button'
                              className='btn btn-link p-0 m-0 pb-1'
                              onClick={() => handleSort('statusLabel')}
                              disabled={records.length === 0}
                            >
                              <KTSVG
                                path={`/media/map/sort-col-${
                                  sortColumn === 'statusLabel'
                                    ? sortOrder === 'asc'
                                      ? 'up-black'
                                      : 'down-black'
                                    : 'grey'
                                }.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>

                        {/* Attachments */}
                        <th style={{minWidth: '140px'}}>
                          Supporting Docs
                        </th>

                        {/* Actions */}
                        <th style={{minWidth: '160px', textAlign: 'center'}}>
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className='table-body text-start'>
                      {(bootLoading || loading) ? (
                        Array.from({length: 8}).map((_, i) => (
                          <tr key={`skeleton-${i}`}>
                            {Array.from({length: columnCount}).map((__, c) => (
                              <td key={`s-${i}-${c}`} className='py-3'>
                                <div className='placeholder-wave'>
                                  <div
                                    className='placeholder w-100'
                                    style={{height: 14, borderRadius: 4}}
                                  />
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (currentRecords.length === 0 && noDataDelayPassed) ? (
                        <tr>
                          <td colSpan={columnCount} className='text-center py-5 text-muted'>
                            No near miss reports found for the selected criteria.
                          </td>
                        </tr>
                      ) : (
                        currentRecords.map((r, idx) => {
                          const reportNo = r.reportNumber || ''
                          const isVType = reportNo.startsWith('V-')
                          const isOType = reportNo.startsWith('O-')

                          const isSaveLike = !r.status || r.status === 'SAVE'

                          const reportMatchesProfileScope =
                            isSuperAdmin ||
                            (isVType && shipboardUser) ||
                            (isOType && !shipboardUser) ||
                            (!isVType && !isOType)
                          const canEditRow =
                            isSaveLike && canUpdateNearMiss && reportMatchesProfileScope
                          const canSubmitRow =
                            isSaveLike && canSubmitNearMiss && reportMatchesProfileScope

                          return (
                            <tr key={r.id}>
                              {/* Sr/No */}
                              <td className='text-center'>
                                {indexOfFirstRecord + idx + 1}
                              </td>

                              {/* Report No */}
                              <td className='text-nowrap'>
                                {r.reportNumber || '-'}
                              </td>

                              {/* Vessel */}
                              {showVesselColumn && (
                                <td className='fw-bold text-nowrap'>
                                  {r.vesselName}
                                </td>
                              )}

                              {/* Occurrence Types */}
                              <td>
                                <span
                                  title={r.occurrenceTypesLabel || '-'}
                                  style={{
                                    maxWidth: '220px',
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  {r.occurrenceTypesLabel || '-'}
                                </span>
                              </td>

                              {/* Date of Occurrence */}
                              <td className='text-nowrap'>
                                {r.dateOfOccurrence || '-'}
                                {r.timeOfOccurrence && (
                                  <span className='text-muted ms-1'>
                                    {r.timeOfOccurrence}
                                  </span>
                                )}
                              </td>

                              {/* Date of Report */}
<td className='text-nowrap'>
  {r.dateReported || '-'}
  {r.timeReported && (
    <span className='text-muted ms-1'>
      {r.timeReported}
    </span>
  )}
</td>

                              {/* Location */}
                              <td>
                                <span
                                  title={r.locationPosition || '-'}
                                  style={{
                                    maxWidth: '200px',
                                    display: 'inline-block',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    verticalAlign: 'middle',
                                  }}
                                >
                                  {r.locationPosition || '-'}
                                </span>
                              </td>

                              {/* Reported By */}
                              <td className='text-nowrap'>
                                {r.reportedBy || '-'}
                              </td>

                              {/* Status */}
                              <td className='text-nowrap'>
                                {r.status ? (
                                  <span className={`badge ${statusBadgeClass(r.status, shipboardUser)}`}>
                                    {renderStatusLabel(r)}
                                  </span>
                                ) : (
                                  '-'
                                )}
                              </td>

                              {/* Supporting Docs */}
                              <td className='text-center'>
                                {r.supportingDocsPrimaryPath ? (
                                  canOpenSupportingDocs ? (
                                    <button
                                      type='button'
                                      className='btn btn-light-primary btn-sm d-inline-flex align-items-center text-nowrap'
                                      onClick={() => openSupportingListModal(r.id, r.reportNumber || null)}
                                    >
                                      <KTSVG
                                        path='/media/icons/duotune/files/fil003.svg'
                                        className='svg-icon-3 me-1'
                                      />
                                      View Files
                                    </button>
                                  ) : (
                                    '-'
                                  )
                                ) : (
                                  '-'
                                )}
                              </td>

                              {/* Actions */}
                              <td style={{textAlign: 'left'}} className='text-nowrap'>
                                <div className='d-flex justify-content-left gap-1'>
                                  {canReadNearMiss ? (
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='View'
                                      onClick={() => openView(r.id)}
                                    >
                                      <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                                    </button>
                                  ) : null}

                                  {canEditRow ? (
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='Edit'
                                      onClick={() => openEdit(r.id)}
                                    >
                                      <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                    </button>
                                  ) : null}

                                  {canExportNearMiss ? (
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='Download Individual Report PDF'
                                      disabled={downloadingPdfId === r.id}
                                      onClick={() => handleDownloadIndividualReportPdf(r.id)}
                                    >
                                      {downloadingPdfId === r.id ? (
                                        <span
                                          className='spinner-border spinner-border-sm text-primary'
                                          style={{width: '14px', height: '14px'}}
                                        />
                                      ) : (
                                        <KTSVG
                                          path='/media/icons/duotune/files/fil021.svg'
                                          className='svg-icon-3 text-primary'
                                        />
                                      )}
                                    </button>
                                  ) : null}

                                  {canSubmitRow ? (
                                    <button
                                      type='button'
                                      className='btn btn-light-primary btn-sm'
                                      title='Submit Report'
                                      onClick={() => setSubmitId(r.id)}
                                    >
                                      Submit
                                    </button>
                                  ) : null}

                                  {r.status === 'SUBMIT' && canCloseNearMiss && (
                                    <button
                                      type='button'
                                      className='btn btn-light-warning btn-sm'
                                      title='Review / Close Near Miss'
                                      onClick={() => setClosingReport(r)}
                                    >
                                      Review / Close
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div
                  className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top'
                  style={{
                    position: 'static',
                    bottom: 0,
                    backgroundColor: '#fff',
                    zIndex: 10,
                  }}
                >
                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-2'>Rows per page</span>
                    <SearchableSelect
                      className='form-select'
                      style={{
                        borderRadius: '20px',
                        width: '70px',
                        border: '1px solid #dee2e6',
                        fontSize: '14px',
                        padding: '4px 8px',
                      }}
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </SearchableSelect>
                  </div>

                  <div className='d-flex align-items-center'>
                    <span className='text-muted me-3' style={{fontSize: '14px'}}>
                      Showing{' '}
                      <strong>
                        {sortedData.length === 0
                          ? 0
                          : ((currentPage - 1) * rowsPerPage) + 1}
                        -
                        {Math.min(currentPage * rowsPerPage, sortedData.length)}
                      </strong>{' '}
                      of <strong>{sortedData.length}</strong>
                    </span>

                    <nav>
                      <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>
                        </li>

                        {(() => {
                          const pages = []
                          const showPages = 5
                          let startPage = Math.max(1, currentPage - 2)
                          let endPage = Math.min(totalPages, startPage + showPages - 1)

                          if (endPage - startPage < showPages - 1) {
                            startPage = Math.max(1, endPage - showPages + 1)
                          }

                          if (startPage > 1) {
                            pages.push(
                              <li key={1} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(1)}
                                >
                                  1
                                </button>
                              </li>
                            )

                            if (startPage > 2) {
                              pages.push(
                                <li key='ellipsis1' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }
                          }

                          for (let i = startPage; i <= endPage; i++) {
                            pages.push(
                              <li
                                key={i}
                                className={`page-item ${currentPage === i ? 'active' : ''}`}
                              >
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                    outline: 'none',
                                    boxShadow: 'none',
                                  }}
                                  onClick={() => handlePageChange(i)}
                                >
                                  {i}
                                </button>
                              </li>
                            )
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <li key='ellipsis2' className='page-item disabled'>
                                  <span
                                    className='page-link border-0 text-muted'
                                    style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                  >
                                    ...
                                  </span>
                                </li>
                              )
                            }

                            pages.push(
                              <li key={totalPages} className='page-item'>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    minWidth: '40px',
                                    borderRadius: '6px',
                                  }}
                                  onClick={() => handlePageChange(totalPages)}
                                >
                                  {totalPages}
                                </button>
                              </li>
                            )
                          }

                          return pages
                        })()}

                        <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                          <button
                            className='page-link text-muted'
                            style={{
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              padding: '8px 12px',
                              fontSize: '14px',
                              borderRadius: '6px',
                            }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add Near Miss Modal */}
<AddNearMissModal
  visible={addOpen}
  isCrew={shipboardUser}
  vessels={vesselsScoped}               // full allowed list for this user
  companies={companies}
  subcompanies={subcompanies}
  isTopLevel={isTopLevel}
  effectiveCompanyId={effectiveCompanyId ?? undefined}
  defaultVesselId={shipboardUser ? currentUser?.vessel?.id : undefined}
  onCancel={() => setAddOpen(false)}
  onSubmit={handleAddNearMiss}
/>


          {/* View Near Miss Modal */}
                   {/* View Near Miss Modal */}
          <ViewNearMissModal
            visible={viewOpen}
            record={viewing}
            onCancel={() => {
              setViewOpen(false)
              setViewing(null)
            }}
            onOpenSupporting={canOpenSupportingDocs ? openSupportingListModal : undefined}
            onDownloadPdf={canExportNearMiss ? handleDownloadIndividualReportPdf : undefined}
            downloadingPdf={Boolean(viewing?.id && downloadingPdfId === viewing.id)}
          />

          {/* Edit Near Miss Modal */}
          <EditNearMissModal
            visible={editOpen}
            isCrew={shipboardUser}
            vessels={vesselsForFilters}
            record={editing}
            onCancel={() => {
              setEditOpen(false)
              setEditing(null)
            }}
            onSubmit={handleUpdateNearMiss}
          />

                    {/* Submit confirmation modal – same UX as DefectSubmitModal */}
          <NearMissSubmitModal
            visible={submitId !== null}
            onCancel={() => setSubmitId(null)}
            onConfirm={async () => {
              if (submitId == null) return
              await handleSubmitNearMissRow(submitId)
              setSubmitId(null)
            }}
          />

          {/* Closure Modal */}
          <NearMissClosureModal
            visible={!!closingReport}
            record={closingReport}
            onCancel={() => setClosingReport(null)}
            onSaveReview={handleSaveReview}
            onCloseWithResult={handleCloseWithResult}
          />

          {/* Shared File Viewer */}
          <FileViewerModal
  visible={fileModal.visible}
  title={fileModal.title}
  fileName={fileModal.fileName}
  viewUrl={fileModal.viewUrl}
  downloadUrl={fileModal.downloadUrl}
  onClose={() =>
    setFileModal(prev => ({
      ...prev,
      visible: false,
    }))
  }
/>

<NearMissAttachmentsModal
  visible={attachmentsModal.visible}
  reportNumber={attachmentsModal.reportNumber}
  loading={attachmentsModal.loading}
  items={attachmentsModal.items}
  onClose={() =>
    setAttachmentsModal(prev => ({...prev, visible: false}))
  }
/>


          {/* Attachments list modal */}
          {/* {attachmentsModal.visible && (
            <div className='modal fade show d-block' tabIndex={-1} role='dialog'>
              <div className='modal-dialog modal-lg' role='document'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title'>
                      Supporting Documents – {attachmentsModal.reportNumber ?? ''}
                    </h5>
                    <button
                      type='button'
                      className='btn-close'
                      aria-label='Close'
                      onClick={() =>
                        setAttachmentsModal({
                          visible: false,
                          reportNumber: null,
                          loading: false,
                          items: [],
                        })
                      }
                    />
                  </div>
                  <div className='modal-body'>
                    {attachmentsModal.loading ? (
                      <div className='d-flex justify-content-center py-4'>
                        {spinner}
                      </div>
                    ) : attachmentsModal.items.length === 0 ? (
                      <div className='text-muted text-center py-3'>
                        No supporting documents uploaded.
                      </div>
                    ) : (
                      <table className='table table-sm align-middle'>
                        <thead>
                          <tr>
                            <th>File Name</th>
                            <th style={{width: '180px'}}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attachmentsModal.items.map(it => (
                            <tr key={it.fileName}>
                              <td>{it.fileName}</td>
                              <td>
                                <div className='d-flex gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-light-primary btn-sm'
                                    onClick={() =>
                                      setFileModal({
                                        visible: true,
                                        title: it.fileName,
                                        fileName: it.fileName,
                                        viewUrl: it.viewUrl,
                                        downloadUrl: it.downloadUrl,
                                      })
                                    }
                                  >
                                    View
                                  </button>
                                  <a
                                    href={it.downloadUrl}
                                    className='btn btn-light-secondary btn-sm'
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    Download
                                  </a>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div className='modal-footer'>
                    <button
                      type='button'
                      className='btn btn-light'
                      onClick={() =>
                        setAttachmentsModal({
                          visible: false,
                          reportNumber: null,
                          loading: false,
                          items: [],
                        })
                      }
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </div>
      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{zIndex: 99999}}
      />
    </div>
  )
}

export default NearMissReports
